import { PaginatedResponse } from './pagination.model';
import { AddressResult } from './address.model';

export type { AddressResult } from './address.model';

// ==============================|| ORDER MODELS ||============================== //

export enum OrderStatus {
  Pending = 'Pending',
  Paid = 'Paid',
  Processing = 'Processing',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
  Cancelled = 'Cancelled',
  Returned = 'Returned'
}

export const ALL_ORDER_STATUSES = Object.values(OrderStatus);

export type OrderListKind = 'current' | 'completed' | 'cancelled' | 'returned';

export const ORDER_LIST_KINDS: OrderListKind[] = ['current', 'completed', 'cancelled', 'returned'];

export const ORDER_KIND_ENDPOINTS: Record<OrderListKind, 'CURRENT' | 'COMPLETED' | 'CANCELLED' | 'RETURNED'> = {
  current: 'CURRENT',
  completed: 'COMPLETED',
  cancelled: 'CANCELLED',
  returned: 'RETURNED'
};

/** Map a concrete order status to its list-tab kind (for dashboard deep-links). */
export function getOrderListKindForStatus(status: OrderStatus | string): OrderListKind {
  switch (status) {
    case OrderStatus.Delivered:
      return 'completed';
    case OrderStatus.Cancelled:
      return 'cancelled';
    case OrderStatus.Returned:
      return 'returned';
    default:
      return 'current';
  }
}

/**
 * Admin can move an order to any status from any status - the backend
 * (Order.AdminSetStatus) has no workflow restriction for the admin path.
 * Only the current status is excluded, since picking it would be a no-op.
 */
export function getValidNextStatuses(currentStatus: OrderStatus | string): OrderStatus[] {
  return ALL_ORDER_STATUSES.filter((status) => status !== currentStatus);
}

export function canAdminCancel(status: OrderStatus | string): boolean {
  return status !== OrderStatus.Delivered && status !== OrderStatus.Cancelled;
}

export interface OrderItemResult {
  productId: string;
  productName: string;
  /** Product accounting code (SKU). */
  sku?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface AppliedCouponResult {
  couponId: string;
  couponCode: string;
  discountAmount: number;
}

export interface OrderStatusHistoryDto {
  status: string;
  statusDisplay: string;
  note?: string;
  changedAt: string;
  changedAtPersian: string;
}

export interface OrderResult {
  id: string;
  orderNumber: string;
  status: OrderStatus | string;
  addressId: string;
  shippingAddress?: AddressResult;
  subTotal: number;
  shippingCost: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  items: OrderItemResult[];
  createdAt: string;
  cancelledAt?: string;
  cancelReason?: string;
  appliedCoupons: AppliedCouponResult[];
  trackingCode?: string;
  statusHistory?: OrderStatusHistoryDto[];
}

export interface OrderFilter {
  page?: number;
  pageSize?: number;
  userId?: string;
  searchTerm?: string;
}

export type OrdersListResponse = PaginatedResponse<OrderResult>;

export interface UpdateOrderStatusRequest {
  status: OrderStatus | string;
  note?: string;
  /** Required by the API when status is Shipped. */
  trackingCode?: string;
}

export interface AddTrackingCodeRequest {
  trackingCode: string;
}

export interface AdminCancelOrderRequest {
  reason: string;
  returnToStock?: boolean;
}

export interface OrderSummaryDto {
  totalAmount: number;
  formattedAmount: string;
  paymentMethod: string;
  shippingAddress: AddressTrackingDto;
  items: OrderItemTrackingDto[];
}

export interface AddressTrackingDto {
  fullAddress: string;
  fullName?: string;
  phoneNumber?: string;
}

export interface OrderItemTrackingDto {
  productName: string;
  quantity: number;
}

export interface OrderTrackingResult {
  orderId: string;
  orderNumber: string;
  currentStatus: string;
  currentStatusDisplay: string;
  estimatedDeliveryDate?: string;
  trackingCode?: string;
  statusHistory: OrderStatusHistoryDto[];
  orderSummary: OrderSummaryDto;
}
